/**
 * Helpers puros da Price Activity — diff, direcção e agregados.
 */

import { midXats } from '@/lib/power-collection-types';
import type {
  PriceActivityChange,
  PriceActivityCategorySummary,
  PriceActivityDirection,
  PriceActivityListFilter,
  PriceActivitySnapshot,
  PriceActivityTrend,
  PriceRange,
} from '@/lib/price-activity-types';
import type { FairtradePowerRow } from '@/lib/fairtrade-catalog';

export function rangeOf(min: number, max: number): PriceRange {
  return { min, max };
}

export function rangesEqual(a: PriceRange, b: PriceRange): boolean {
  return a.min === b.min && a.max === b.max;
}

function midRange(range: PriceRange): number {
  return midXats(range.min, range.max);
}

function rangeDirection(prev: PriceRange, next: PriceRange): 'up' | 'down' | 'same' {
  const a = midRange(prev);
  const b = midRange(next);
  if (b > a) return 'up';
  if (b < a) return 'down';
  return 'same';
}

/** Combina direcções de xats/days numa direcção de item. */
export function combineDirections(
  xatsDir: 'up' | 'down' | 'same' | null,
  daysDir: 'up' | 'down' | 'same' | null
): PriceActivityDirection | null {
  const dirs = [xatsDir, daysDir].filter(
    (d): d is 'up' | 'down' => d === 'up' || d === 'down'
  );
  if (dirs.length === 0) return null;
  if (dirs.every((d) => d === 'up')) return 'up';
  if (dirs.every((d) => d === 'down')) return 'down';
  return 'mixed';
}

export function snapshotFromRow(
  row: FairtradePowerRow,
  fetchedAt = new Date().toISOString()
): PriceActivitySnapshot {
  return {
    powerId: row.id,
    name: row.name,
    shortname: row.name,
    category: row.category,
    typeLabel: row.typeLabel,
    minXats: row.minXats,
    maxXats: row.maxXats,
    minDays: row.minDays,
    maxDays: row.maxDays,
    fetchedAt,
  };
}

/**
 * Compara snapshot anterior com a row actual.
 * Devolve null se não houve mudança real de xats/days.
 */
export function diffRowAgainstSnapshot(
  row: FairtradePowerRow,
  prev: PriceActivitySnapshot,
  timestamp = new Date().toISOString()
): PriceActivityChange | null {
  const previousXats = rangeOf(prev.minXats, prev.maxXats);
  const currentXats = rangeOf(row.minXats, row.maxXats);
  const previousDays = rangeOf(prev.minDays, prev.maxDays);
  const currentDays = rangeOf(row.minDays, row.maxDays);

  const xatsChanged = !rangesEqual(previousXats, currentXats);
  const daysChanged = !rangesEqual(previousDays, currentDays);
  if (!xatsChanged && !daysChanged) return null;

  const xatsDir = xatsChanged ? rangeDirection(previousXats, currentXats) : null;
  const daysDir = daysChanged ? rangeDirection(previousDays, currentDays) : null;
  const direction = combineDirections(xatsDir, daysDir);
  if (!direction) return null;

  return {
    id: `${row.id}-${timestamp}`,
    powerId: row.id,
    powerName: row.name,
    shortname: row.name,
    category: row.category,
    typeLabel: row.typeLabel,
    timestamp,
    previousXats,
    currentXats,
    previousDays,
    currentDays,
    xatsChanged,
    daysChanged,
    direction,
  };
}

/** True se o change novo é idêntico ao último registado (dedup). */
export function isDuplicateChange(
  latest: PriceActivityChange | undefined,
  next: PriceActivityChange
): boolean {
  if (!latest) return false;
  return (
    latest.powerId === next.powerId &&
    rangesEqual(latest.previousXats, next.previousXats) &&
    rangesEqual(latest.currentXats, next.currentXats) &&
    rangesEqual(latest.previousDays, next.previousDays) &&
    rangesEqual(latest.currentDays, next.currentDays)
  );
}

export function summarizeCategoryChanges(
  category: string,
  changes: PriceActivityChange[]
): PriceActivityCategorySummary {
  let upCount = 0;
  let downCount = 0;
  let mixedCount = 0;

  for (const c of changes) {
    if (c.direction === 'up') upCount += 1;
    else if (c.direction === 'down') downCount += 1;
    else mixedCount += 1;
  }

  const total = changes.length;
  let trend: PriceActivityTrend = 'neutral';
  if (total > 0) {
    if (upCount > downCount && upCount >= mixedCount) trend = 'up';
    else if (downCount > upCount && downCount >= mixedCount) trend = 'down';
    else trend = 'mixed';
  }

  return { category, total, upCount, downCount, mixedCount, trend };
}

export function filterPriceActivityChanges(
  changes: PriceActivityChange[],
  filter: PriceActivityListFilter
): PriceActivityChange[] {
  return changes.filter((c) => {
    if (filter.direction === 'up' && c.direction !== 'up') return false;
    if (filter.direction === 'down' && c.direction !== 'down') return false;
    if (filter.metric === 'xats' && !c.xatsChanged) return false;
    if (filter.metric === 'days' && !c.daysChanged) return false;
    return true;
  });
}

export function sortChangesNewestFirst(
  changes: PriceActivityChange[]
): PriceActivityChange[] {
  return [...changes].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

export function formatPriceRangeLabel(range: PriceRange): string {
  if (!range.min && !range.max) return '—';
  if (range.min === range.max || !range.min || !range.max) {
    return String(range.min || range.max);
  }
  return `${range.min.toLocaleString('en-US')}–${range.max.toLocaleString('en-US')}`;
}

export function badgeTrendSymbol(trend: PriceActivityTrend): string {
  if (trend === 'up') return '↗';
  if (trend === 'down') return '↘';
  if (trend === 'mixed') return '↕';
  return '·';
}
